import { useRouter } from "@uirouter/react";
import useIsMounted from "ismounted";
import React, { useContext, useEffect } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { PrimaryButton } from "../../../../components/button";
import { useFileLoader } from "../../../../components/hooks/file-loader";
import { useAppLoader } from "../../../../components/loader/app-loader-hook";
import { StepperContext } from "../../../../components/stepper/context";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { LaunchConfigurationsService } from "../../../../store/launch-configurations/service";
import { NotificationActions } from "../../../../store/notifications/actions";
import { LaunchConfigurationsRoutes } from "../../routes";
import { Container } from "../common/components";
import { FormType } from "../common/form";
import { useDragCallbacks } from "./hooks";
import SinglePage from "./single-page";

const AddPageButton = styled(PrimaryButton)`
  margin-top: 2em;
`;

export const Pages: React.FunctionComponent<{
  updateId?: string;
}> = ({ updateId }) => {
  const { setValidation } = useContext(StepperContext);
  const isMounted = useIsMounted();

  const schema = LaunchConfigurationsDTO.Requests.SetSingle.useSchema();
  const router = useRouter();
  const { control, handleSubmit } = useFormContext<FormType>();
  const dispatch = useDispatch();

  const normalLoader = useAppLoader();
  const fileLoader = useFileLoader();

  useEffect(() => {
    setValidation(() => async () => {
      return await new Promise<void>((res, rej) => {
        handleSubmit(
          async (allData) => {
            try {
              if (updateId) {
                await normalLoader(
                  LaunchConfigurationsService.updateLaunchConfiguration(
                    updateId,
                    allData
                  )
                );
                if (allData.headerImage) {
                  await fileLoader(
                    LaunchConfigurationsService.addHeaderImage(updateId, {
                      headerImage: allData.headerImage,
                    })
                  );
                }

                await router.stateService.reload();
              } else {
                const response = await LaunchConfigurationsService.postLaunchConfiguration(
                  allData
                );
                if (allData.headerImage) {
                  await fileLoader(
                    LaunchConfigurationsService.addHeaderImage(response.id, {
                      headerImage: allData.headerImage,
                    })
                  );
                }
                router.stateService.go(
                  LaunchConfigurationsRoutes.names.single,
                  {
                    id: response.id,
                  }
                );
              }
              res();
            } catch (e: any) {
              rej(e);
            }
          },
          (err) => {
            dispatch(
              NotificationActions.setNotification({
                title: "An error has occured",
                variant: "error",
              })
            );
            console.error("Form is invalid: ");
            console.error(err);
          }
        )();
      });
    });

    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    setValidation,
    schema,
    updateId,
    handleSubmit,
    router,
    dispatch,
    normalLoader,
    fileLoader,
  ]);

  const pages = useFieldArray({
    control,
    name: "pages",
  });

  const { pageRefs, onDragEnd } = useDragCallbacks(pages);

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Container>
          {pages.fields.map(({ id }, index) => (
            <SinglePage
              pageNumber={index}
              key={id}
              removePage={() => {
                pageRefs.current?.delete(index);
                pages.remove(index);
              }}
              id={id}
              ref={(el) => pageRefs.current?.set(index, el)}
            />
          ))}
          <AddPageButton
            fullWidth
            onClick={() => {
              pages.append({
                fields: [],
                title: {},
              });
            }}
          >
            Add Page
          </AddPageButton>
        </Container>
      </DragDropContext>
    </>
  );
};
